/**
 * Tiny registry that lets non-Chart components (currently:
 * TradeJournal) snapshot a Chart's rendered canvas without
 * threading refs through Redux.
 *
 * Each Chart.tsx instance calls `registerCapture(tabId, fn)` on
 * mount and `unregisterCapture(tabId)` on unmount. The capture fn
 * closes over the chart's own wrapper ref + html2canvas config
 * (which already knows to ignore the context menu, preserve the
 * dark background, etc), so consumers don't need to re-implement
 * any of that.
 *
 * `captureChart(tabId)` resolves to a ready-to-upload blob plus a
 * suggested filename, or null if no chart is registered for that
 * tab (race during tab teardown).
 */
import { nowFileStamp } from "./chartTime";

export interface ChartCaptureResult {
  blob: Blob;
  filename: string;
  symbol: string;
}

type CaptureFn = () => Promise<{ blob: Blob | null; symbol: string }>;

const registry: Map<string, CaptureFn> = new Map();

export function registerCapture(tabId: string, fn: CaptureFn): void {
  registry.set(tabId, fn);
}

export function unregisterCapture(tabId: string): void {
  registry.delete(tabId);
}

export async function captureChart(
  tabId: string,
): Promise<ChartCaptureResult | null> {
  const fn = registry.get(tabId);
  if (!fn) return null;
  const { blob, symbol } = await fn();
  if (!blob) return null;
  // Filename: tickview_SYMBOL_YYYY-MM-DDTHH-MM-SS.png (UTC, via chartTime).
  return {
    blob,
    filename: `tickview_${symbol}_${nowFileStamp()}.png`,
    symbol,
  };
}

// -----------------------------------------------------------------
// Chart navigation registry — lets non-Chart components imperatively
// pan a Chart to a time range without round-tripping through Redux.
//
// Used by the TradeJournal "Jump to trade" button: same-symbol jumps
// pan immediately; cross-symbol jumps still go through Redux (the
// data refetch path applies the saved viewport on fresh-load), so
// this registry is the snappy path for the common case.
// -----------------------------------------------------------------

type PanFn = (from: number, to: number) => void;
const panRegistry: Map<string, PanFn> = new Map();

export function registerChartPan(tabId: string, fn: PanFn): void {
  panRegistry.set(tabId, fn);
}

export function unregisterChartPan(tabId: string): void {
  panRegistry.delete(tabId);
}

/** Pan the chart for `tabId` to show `from`..`to` (UTC seconds).
 *  Returns true if the chart was registered and the call dispatched;
 *  callers can use the return value to decide whether a Redux-based
 *  fallback (e.g., dispatch setSymbol → data refetch → viewport
 *  restore on fresh-load) is needed. */
export function panChartTo(
  tabId: string,
  from: number,
  to: number,
): boolean {
  const fn = panRegistry.get(tabId);
  if (!fn) return false;
  fn(from, to);
  return true;
}
